import { useState, useCallback, useEffect } from 'react';
import { api } from '../utils/api';
import toast from 'react-hot-toast';
import { useConfirmStore } from '../store/confirmStore';

export interface CableEdge {
  cable_id: string;
  source_device: string;
  source_device_id?: string | null;
  source_port: string;
  source_port_id?: string | null;
  target_device: string;
  target_device_id?: string | null;
  target_port: string;
  target_port_id?: string | null;
  cable_type: string;
  color: string;
  length_m: number | null;
  status: string;
  netbox_id?: number | null;
}

export function useCablesList(dcCode: string | undefined) {
  const [edges, setEdges] = useState<CableEdge[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchCables = useCallback(async () => {
    if (!dcCode) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/datacenters/${dcCode}/connectivity/graph`);
      setEdges(res.data.edges || []);
    } catch (err) {
      console.error('Failed to fetch connectivity graph:', err);
      toast.error('Ошибка загрузки соединений');
    } finally {
      setLoading(false);
    }
  }, [dcCode]);

  useEffect(() => {
    fetchCables();
  }, [fetchCables]);

  const handleDelete = async (cableId: string) => {
    const confirmed = await useConfirmStore.getState().confirm({
      title: 'Удаление кабельного соединения',
      message: 'Вы уверены, что хотите удалить это кабельное соединение?',
      confirmText: 'Удалить',
      cancelText: 'Отмена'
    });
    if (!confirmed) {
      return;
    }
    setDeletingId(cableId);
    try {
      await api.delete(`/api/datacenters/${dcCode}/connectivity/cables/${cableId}`);
      setEdges(prev => prev.filter(e => e.cable_id !== cableId));
      toast.success('Соединение успешно удалено');
    } catch (err: any) {
      console.error(err);
      toast.error('Не удалось удалить соединение: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingId(null);
    }
  };

  return {
    edges,
    loading,
    deletingId,
    fetchCables,
    handleDelete
  };
}
